import React, { useState } from 'react';
import Icon from '../../../components/AppIcon';

const GrapheneSection = () => {
  const [expanded, setExpanded] = useState(false);

  const claims = [
    {
      icon: 'Repeat',
      label: 'Laadcycli',
      value: '20.000 cycli',
      sub: 'Ontworpen voor 20.000 laadcycli (gemeten bij 25°C) — meer dan 3× langer dan standaard LFP',
      color: 'bg-green-50 border-green-200',
      iconColor: 'text-green-600',
      iconBg: 'bg-green-100',
    },
    {
      icon: 'Zap',
      label: 'Laadsnelheid',
      value: '100 A continu',
      sub: '200 A piekontlading (10 s bij 50% SOC) — snel laden en ontladen',
      color: 'bg-amber-50 border-amber-200',
      iconColor: 'text-amber-600',
      iconBg: 'bg-amber-100',
    },
    {
      icon: 'ShieldCheck',
      label: 'Veiligheid',
      value: 'UN 38.3 getest',
      sub: 'Kiwa Primara rapport 24PP484-01 — hoogtesimulatie, thermisch, vibratie, schok, kortsluiting',
      color: 'bg-blue-50 border-blue-200',
      iconColor: 'text-blue-600',
      iconBg: 'bg-blue-100',
    },
    {
      icon: 'Thermometer',
      label: 'Temperatuurbereik',
      value: '−20°C tot +55°C',
      sub: 'Ontladen tot −20°C, laden van 0°C tot +55°C — ook in de winter optimaal',
      color: 'bg-purple-50 border-purple-200',
      iconColor: 'text-purple-600',
      iconBg: 'bg-purple-100',
    },
  ];

  const features = [
    { icon: 'Volume2', label: 'Stille passieve koeling', desc: 'Geen ventilator, geen geluid — volledig passief gekoeld via natuurlijke convectie' },
    { icon: 'Layers', label: 'Modulair & schaalbaar', desc: 'Stapelbaar van 5,6 kWh tot 56 kWh (tot 10 modules parallel)' },
    { icon: 'Cpu', label: 'Cel-niveau BMS', desc: 'Batterijbeheersysteem met monitoring op celniveau via CAN/RS485' },
    { icon: 'Home', label: 'Stalen behuizing', desc: 'Robuuste stalen behuizing, wandmontage of stapelbaar vloermodel' },
    { icon: 'Battery', label: 'Lage zelfontlading', desc: 'Slechts 2% zelfontlading per maand — energie blijft beschikbaar' },
    { icon: 'Award', label: '15 jaar garantie', desc: 'Op het stapelbare model — te verifiëren voor het wandmodel' },
  ];

  return (
    <section className="py-16 lg:py-24 bg-gray-50">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-start">
          {/* Left */}
          <div className="space-y-6">
            <div className="inline-flex items-center space-x-2 text-primary bg-primary/10 px-3 py-1.5 rounded-full text-xs font-heading font-semibold uppercase tracking-wider">
              <Icon name="Atom" size={12} />
              <span>Technologische voorsprong</span>
            </div>
            <h2 className="text-3xl sm:text-4xl font-heading font-bold text-foreground leading-tight">
              Grafeen: de volgende generatie energieopslag — nu al aan je muur
            </h2>
            <p className="font-body text-muted-foreground leading-relaxed">
              Grafeen is een materiaal dat bestaat uit één laag koolstofatomen in een hexagonale structuur. Het is extreem geleidend, licht en sterk — eigenschappen die batterijen fundamenteel verbeteren.
            </p>

            {/* Expandable */}
            {expanded && (
              <div className="bg-white border border-border rounded-xl p-5 space-y-3">
                <p className="text-sm font-body text-foreground leading-relaxed">
                  Grafeen-elektroden zorgen voor snellere ionentransport, minder warmteontwikkeling en een langere cyclische levensduur. Dit vertaalt zich direct naar een batterij die langer meegaat, sneller laadt en veiliger is bij extreme omstandigheden.
                </p>
                <p className="text-sm font-body text-muted-foreground leading-relaxed">
                  De GRP-grafeencel (31 Ah) heeft bij Kiwa Primara de volledige UN 38.3-testreeks doorstaan: hoogtesimulatie, thermische test, vibratie, schok, kortsluiting, crush en geforceerde ontlading. De GRP Graphene batterij is ontworpen voor 20.000 laadcycli — meer dan 3× de levensduur van een standaard LFP-batterij (6.000 cycli).
                </p>
                <p className="text-xs font-caption text-muted-foreground italic">
                  ⚠️ Het UN 38.3-rapport betreft transportveiligheidstests van de cel — niet een productcertificering van het complete systeem. De 20.000 cycli zijn een ontwerpdoelstelling gemeten bij 25°C.
                </p>
              </div>
            )}

            <button
              onClick={() => setExpanded(!expanded)}
              className="inline-flex items-center text-sm font-heading font-semibold text-primary hover:text-primary/80 transition-colors"
            >
              {expanded ? 'Minder lezen' : 'Lees meer over grafeen'}
              <Icon name={expanded ? 'ChevronUp' : 'ChevronDown'} size={15} className="ml-1" />
            </button>

            {/* Features grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              {features?.map((feat) => (
                <div key={feat?.label} className="bg-white border border-border rounded-xl p-4 flex items-start space-x-3">
                  <div className="w-8 h-8 bg-primary/10 rounded-lg flex items-center justify-center flex-shrink-0 mt-0.5">
                    <Icon name={feat?.icon} size={15} className="text-primary" />
                  </div>
                  <div>
                    <p className="text-sm font-heading font-semibold text-foreground">{feat?.label}</p>
                    <p className="text-xs font-body text-muted-foreground mt-0.5 leading-relaxed">{feat?.desc}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* Kiwa evidence block */}
            <div className="bg-white border-2 border-blue-200 rounded-2xl p-6 shadow-elevation-1">
              <div className="flex items-start space-x-4">
                <div className="w-12 h-12 bg-blue-50 rounded-xl flex items-center justify-center flex-shrink-0">
                  <Icon name="Award" size={22} className="text-blue-600" />
                </div>
                <div className="space-y-2">
                  <p className="font-heading font-bold text-foreground">Kiwa Primara — Onafhankelijk getest</p>
                  <p className="text-sm font-body text-muted-foreground leading-relaxed">
                    "De GRP-grafeencel (31 Ah) doorstond bij Kiwa Primara de volledige UN 38.3-testreeks: hoogtesimulatie, thermische test, vibratie, schok, kortsluiting, crush en geforceerde ontlading."
                  </p>
                  <a
                    href="#"
                    className="inline-flex items-center text-sm font-heading font-semibold text-blue-600 hover:text-blue-700 transition-colors"
                  >
                    Bekijk rapport 24PP484-01
                    <Icon name="ExternalLink" size={13} className="ml-1.5" />
                  </a>
                </div>
              </div>
            </div>
          </div>

          {/* Right — 4 claim blocks */}
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              {claims?.map((claim) => (
                <div key={claim?.label} className={`rounded-2xl border p-5 ${claim?.color}`}>
                  <div className={`w-10 h-10 ${claim?.iconBg} rounded-xl flex items-center justify-center mb-4`}>
                    <Icon name={claim?.icon} size={18} className={claim?.iconColor} />
                  </div>
                  <p className="text-xs font-caption text-muted-foreground uppercase tracking-wider mb-1">{claim?.label}</p>
                  <p className="text-xl font-heading font-bold text-foreground leading-tight">{claim?.value}</p>
                  <p className="text-xs font-body text-muted-foreground mt-1 leading-relaxed">{claim?.sub}</p>
                </div>
              ))}
            </div>

            {/* LFP comparison callout */}
            <div className="bg-white border border-border rounded-2xl p-5">
              <p className="text-xs font-caption text-muted-foreground uppercase tracking-wider mb-3">Grafeen vs. gangbare markt — op een rij</p>
              <div className="space-y-2.5">
                {[
                  { label: 'Laadcycli', graphene: '20.000', lfp: '4.000–6.000' },
                  { label: 'Garantie', graphene: '15 jaar*', lfp: '5–10 jaar' },
                  { label: 'Ontladen bij kou', graphene: 'tot −20°C', lfp: 'tot −20°C' },
                  { label: 'Koeling', graphene: 'Passief (stil)', lfp: 'Wisselend' },
                  { label: 'Zelfontlading', graphene: '2% / maand', lfp: 'n.v.t.' },
                ]?.map((row, i) => (
                  <div key={i} className="grid grid-cols-3 gap-2 text-xs">
                    <span className="font-body text-muted-foreground">{row?.label}</span>
                    <span className="font-heading font-semibold text-primary">{row?.graphene}</span>
                    <span className="font-body text-foreground">{row?.lfp}</span>
                  </div>
                ))}
              </div>
              <p className="text-xs font-caption text-muted-foreground italic mt-3">* Garantievoorwaarden te verifiëren met GRP Energy vóór publicatie.</p>
            </div>

            <p className="text-xs font-caption text-muted-foreground italic">
              * Alle technische waarden zijn afkomstig uit de officiële GRP-datasheets en gebruikershandleidingen (versie oktober 2025 – mei 2026). Definitieve waarden worden vóór publicatie geverifieerd.
            </p>
          </div>
        </div>
      </div>
    </section>
  );
};

export default GrapheneSection;
